import React, { useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

import useVoiceRecorder from '../hooks/useVoiceRecorder';
import { CHATBOT_HE } from '../chatbotStrings.he';

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  100% { transform: scale(2.5); opacity: 0; }
`;

const wave = keyframes`
  0%, 100% { height: 4px; }
  50% { height: 20px; }
`;

const PulseRing = styled('span')(({ theme, delay }) => ({
  position: 'absolute',
  borderRadius: '50%',
  border: '2px solid rgba(239, 68, 68, 0.4)',
  width: 20,
  height: 20,
  animation: `${pulse} 2s ease-out infinite`,
  animationDelay: delay,
  pointerEvents: 'none',
}));

const WaveBar = styled('span')(({ delay }) => ({
  width: 3,
  height: 4,
  background: 'linear-gradient(to top, #6b7280, #374151)',
  borderRadius: 2,
  animation: `${wave} 1s ease-in-out infinite`,
  animationDelay: delay,
}));

export default function VoiceRecordButton({
  onVoiceSend,
  disabled,
  onRecordingStateChange,
  compact = false,
}) {
  const { isRecording, formattedTime, startRecording, stopRecording, cancelRecording } = useVoiceRecorder();

  useEffect(() => {
    onRecordingStateChange?.(isRecording);
  }, [isRecording, onRecordingStateChange]);

  const handleStart = async () => {
    try {
      await startRecording();
    } catch {
      // keep behavior same as source widget
      // eslint-disable-next-line no-alert
      alert(CHATBOT_HE.micRequired);
    }
  };

  const handleSend = async () => {
    const blob = await stopRecording();
    if (blob && blob.size > 0) onVoiceSend?.(blob);
  };

  if (isRecording) {
    const size = compact ? 36 : 48;

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: compact ? 1 : 2,
          py: compact ? 1 : 2,
          px: compact ? 1 : 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 2 }}>
          <Box sx={{ position: 'relative', width: compact ? 32 : 40, height: compact ? 32 : 40 }}>
            <PulseRing delay="0s" />
            <PulseRing delay="0.6s" />
            <PulseRing delay="1.2s" />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ width: 16, height: 16, bgcolor: 'error.main', borderRadius: '50%' }} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: compact ? 32 : 40 }}>
            {Array.from({ length: compact ? 8 : 12 }).map((_, i) => (
              <WaveBar key={i} delay={`${i * 0.08}s`} />
            ))}
          </Box>

          <Box sx={{ fontWeight: 800, color: 'text.secondary', minWidth: compact ? 50 : 60, textAlign: 'center' }}>
            {formattedTime}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 2 }}>
          <IconButton
            onClick={cancelRecording}
            size="large"
            sx={{
              width: size,
              height: size,
              bgcolor: 'error.light',
              color: 'error.main',
              '&:hover': { bgcolor: 'error.light' },
            }}
            title={CHATBOT_HE.voiceCancel}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={handleSend}
            size="large"
            sx={{
              width: size,
              height: size,
              bgcolor: '#304fff',
              color: '#fff',
              '&:hover': { bgcolor: '#1e3ad4' },
            }}
            title={CHATBOT_HE.voiceSend}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <IconButton
      onClick={handleStart}
      disabled={disabled}
      sx={{
        width: compact ? 36 : 48,
        height: compact ? 36 : 48,
        bgcolor: '#304fff',
        color: '#fff',
        borderRadius: 2,
        '&:hover': { bgcolor: '#1e3ad4' },
        '&.Mui-disabled': { opacity: 0.4, color: '#fff' },
      }}
      title={CHATBOT_HE.voiceRecord}
    >
      <MicIcon fontSize={compact ? 'small' : 'medium'} />
    </IconButton>
  );
}

